import React, {useState, useEffect, useCallback, useRef} from 'react';
import {Alert, Button, Container, Row, Col, Modal, Spinner} from 'react-bootstrap';

import {Summary, Buttons, Step1, Register, ByAmount, ByTransaction, Memo, AdminFee, Items, Success, TokenHandling} from '.';
import ErrorCatcher from '../../../../components/common/ErrorCatcher';

import APIPOS from '../../../../api/Pos';
import {
    flattenErrorMessage,
    hasSuccessfulResponseData,
    isAmbiguousMutationResponse,
    isTokenOptionsPayload,
    normalizeCurrency,
    sumCurrency,
    toCents,
} from '../refundUtils';
import styles from './Wizard.module.scss';

const maxSteps = 9;
const isValidRefundAmount = (value, maxTotal, allowZero) => {
    if (value === "" || value === null || value === undefined) return false;

    const numericAmount = Number(value);
    const amount = toCents(numericAmount);
    const maximum = toCents(maxTotal);
    return Number.isFinite(numericAmount)
        && numericAmount >= 0
        && amount <= maximum
        && (allowZero ? amount >= 0 : amount > 0);
};

export const Wizard = (props) => {
    const {onHide, redrawOrder} = props;

    const buttonsRef= useRef();
    const submittingRef = useRef(false);
    const submissionBlockedRef = useRef(false);
    
    const [stepData, setStepData] = useState({});
    const [currentStep, setCurrentStep] = useState({step: 1, next: null, back: null});
    const [errors, setErrors] = useState();
    const [success, setSuccess] = useState();
    const [submitting, setSubmitting] = useState(false);
    const [submissionBlocked, setSubmissionBlocked] = useState(false);
    const [tokenOptions, setTokenOptions] = useState(null);
    const [tokenOptionsLoading, setTokenOptionsLoading] = useState(true);
    const [tokenOptionsError, setTokenOptionsError] = useState(null);
    const hasTokens = Boolean(tokenOptions?.has_tokens);

    const loadTokenOptions = useCallback(async () => {
        setTokenOptionsLoading(true);
        setTokenOptionsError(null);

        try {
            const response = await APIPOS.payment.getRefundTokenOptions({order_id: props.order.id});
            if (response?.errors || response?.error || !isTokenOptionsPayload(response?.data)) throw response;

            setTokenOptions(response.data);
            setStepData(previous => {
                const next = {...previous};
                if (response.data.has_tokens) {
                    next.token_handling = previous.token_handling || {confirmed: false, token_ids: []};
                } else {
                    delete next.token_handling;
                }
                return next;
            });
        } catch (error) {
            setTokenOptions(null);
            setTokenOptionsError(flattenErrorMessage(error) || "No usable token information was returned for this order. Please try again.");
        } finally {
            setTokenOptionsLoading(false);
        }
    }, [props.order.id]);

    const lockAfterAmbiguousOutcome = useCallback(error => {
        const detail = flattenErrorMessage(error);
        submissionBlockedRef.current = true;
        setSubmissionBlocked(true);
        setErrors([
            "The refund result could not be confirmed. Do not submit it again from this window. Close this refund window and refresh the order before retrying.",
            ...(detail ? [detail] : []),
        ]);
    }, []);

    const processRefund = useCallback(async ()=>{
        if (submittingRef.current || submissionBlockedRef.current) return;

        let _errors = [];
        if (!stepData.type) _errors.push("Please select refund type.");
        if (stepData.type === "amount" && !isValidRefundAmount(stepData?.amount, props.maxTotal, hasTokens)) {
            _errors.push(hasTokens ? "Enter a financial refund amount from $0 through the maximum allowed refund." : "Amount is required.");
        }
        /*if (stepData.type === "item" && !stepData?.all_items) _errors.push("Please select refund type.");
        else if (stepData.all_items === 2 && (!stepData?.items || !stepData?.items.length)) _errors.push("Please select items to refund.");*/
        if (stepData.type === "item" && !stepData?.items?.length) _errors.push("Please select items to refund.");
        if (hasTokens && stepData.type === "item") _errors.push("Token-bearing orders cannot be refunded by individual item.");
        if (stepData.type === "transaction" && (!stepData?.transactions || !stepData?.transactions.length)) _errors.push("Please select payments to refund.");
        if (+stepData.admin_fee > 0 && !stepData.agreement) _errors.push("You must agree to the terms to issue the cash discount.");
        if (hasTokens && (stepData?.token_handling?.confirmed !== true || !Array.isArray(stepData?.token_handling?.token_ids))) {
            _errors.push("Please confirm how the purchased tokens should be handled.");
        }

        if (_errors.length > 0) {
            setErrors(_errors);
            return;
        }

        submittingRef.current = true;
        setSubmitting(true);

        let refund_amount = 0, items = null, transactions = null;
        if (stepData.type === "amount") refund_amount = normalizeCurrency(stepData.amount);
        else if (stepData.type === "item") {
            /*if (stepData.all_items === 1) { // refunding all items, grab them from the order
                refund_amount = +props.maxTotal;
                items = props.order.items.map(item => ({
                    id: item.id,
                    product_id: item.product_id,
                    variant_id: item.variant_id,
                    product_name: item.product_name,
                    refund_amount: +item.price + +item.tax + +item.shipping,
                    refund_tax: +item.tax || 0,
                    refund_shipping: +item.shipping || 0,
                    refund_price: +item.price || 0,
                    refund_memo: "",
                    refund_type: "full amount"
                }));
            } else if (stepData.all_items === 2) {*/
                refund_amount = sumCurrency(stepData.items.map(item => item.refund_amount));
                items = stepData.items.map(item => ({
                    ...item,
                    refund_amount: normalizeCurrency(item.refund_amount),
                    refund_tax: normalizeCurrency(item.refund_tax),
                    refund_shipping: normalizeCurrency(item.refund_shipping),
                    refund_price: normalizeCurrency(item.refund_price),
                }));
            //}
        } else if (stepData.type === "transaction") {
            refund_amount = sumCurrency(stepData.transactions.map(item => item.refund_amount));
            transactions = stepData.transactions.map(transaction => ({
                ...transaction,
                refund_amount: normalizeCurrency(transaction.refund_amount),
            }));
        }

        try{
            const res = await APIPOS.orderRefund({
                order_id: props.order.id, 
                refund_amount, 
                refund_type: stepData.type,
                items: items || undefined, 
                transactions: transactions || undefined,
                admin_fee: stepData.admin_fee || undefined, 
                register_id: +stepData.register_id || +props.registerId || +props.order.register_id || null,
                memo: stepData?.memo || undefined,
                ...(hasTokens ? {
                    token_handling: {
                        confirmed: true,
                        token_ids: stepData.token_handling.token_ids.map(id => +id),
                    }
                } : {})
            });

            //console.log(res)
            
            if (isAmbiguousMutationResponse(res)) {
                lockAfterAmbiguousOutcome(res);
            } else if (res?.errors) setErrors(res.errors);
            else if (hasSuccessfulResponseData(res)) {
                setSuccess(res.data);
                setErrors(null);
            }
        } catch(e){
            lockAfterAmbiguousOutcome(e);
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    },[stepData, props.maxTotal, props.order, props.registerId, hasTokens, lockAfterAmbiguousOutcome]);

    const allDoneHandler = useCallback((e)=>{
        e.preventDefault();
        setCurrentStep({step: 1, next: null, back: null});
        setStepData({});
        redrawOrder();
        onHide(success);
    }, [onHide, redrawOrder, success]);

    /* determines which steps are next and previous depending on the current step, and which elements need to be reset according to the data*/
    const getNextPrevSteps = useCallback((step, data) => {
        let next = null, back = null, resets = null;
        switch(step){
            case 1: // Step1
                if (data?.type){
                    if (data.type === "amount") next = 2;
                    else if (data.type === "item" && !hasTokens) next = 5;
                    else if (data.type === "transaction") next = 4;
                }
                break;
            case 2: // ByAmount
                back = 1;
                if (data?.type === "amount" && isValidRefundAmount(data?.amount, props.maxTotal, hasTokens)) {
                    next = hasTokens ? 3 : (props.registerId ? 8 : 7);
                    resets = {/*all_items: null,*/ items: null, admin_fee: null, transactions: null};
                } else if (data?.type !== "amount") {
                    resets = {amount: null};
                }
                break;                
            case 3: // TokenHandling
                back = data?.type === "amount" ? 2 : data?.type === "transaction" ? 4 : (props.order.admin_fee_total > 0 ? 6 : 5);
                if (hasTokens && data?.token_handling?.confirmed === true && Array.isArray(data?.token_handling?.token_ids)) {
                    next = props.registerId ? 8 : 7;
                }
                break;
            case 4: // ByTransaction
                back = 1;
                resets = {amount: null, /*all_items: null,*/ items: null, admin_fee: null};
                if (data?.type === "transaction" && data?.transactions?.length > 0) next = hasTokens ? 3 : (props.registerId ? 8 : 7);
                else if (data?.type !== "transaction") resets = {...resets, transactions: null};
                break;
            case 5: // Items
                back = 1;
                resets = {amount: null};
                if (data?.type ==="item" && /*data?.all_items===2 &&*/ data?.items?.length > 0) {
                    if (props.order.admin_fee_total > 0) next = 6;
                    else next = hasTokens ? 3 : (props.registerId ? 8 : 7);
                }
                break;
            case 6: // AdminFee
                resets = {amount: null};
                back = data?.items?.length > 0 ? 5 : 1;
                if (data?.admin_fee > 0 && data?.agreement) next = hasTokens ? 3 : (props.registerId ? 8 : 7);
                break;
            case 7: // Register
                back = hasTokens ? 3 : (data.type === "amount" || data.type==="transaction" ? 2 : props.order.admin_fee_total > 0 ? 6 : 5);
                next = 8;
                break;
            case 8: // Memo
                back = !props.registerId ? 7 : (hasTokens ? 3 : (data.type === "amount" ? 2 : (data.type === "item" ? (props.order.admin_fee_total > 0 ? 6 : 5) : 4 )));
                next = 9;
                break;
            case 9: // Summary
                //back = !props.registerId ? 7 : (data.type === "amount" ? 2 : 6);
                back = 8;
                break;
            default:
                break;
        }
        
        return {next, back, resets};
    }, [props.registerId, props.order.admin_fee_total, props.maxTotal, hasTokens]);

    const stepChangeHandler = useCallback((step, type = "next") => {
        if (step < 1 || !step) step = 1;

        if (type === "submit") processRefund();
        else if (step <= maxSteps){
            const _nextprev = getNextPrevSteps(step, stepData); // get next and prev steps
            setCurrentStep({step, next: _nextprev.next, back: _nextprev.back});
        }
    }, [getNextPrevSteps, processRefund, stepData]);

    const stepUpdateHandler = useCallback((step, data) => {
        setStepData(prev=>{
            prev = {...prev, ...data};
            const _nextprev = getNextPrevSteps(step, prev); // validate and get next and prev steps
            if (_nextprev.resets) prev = {...prev, ..._nextprev.resets}; // reset values that are not supposed to be there (for example, items when the refund type changed to by amount)
            setCurrentStep({step, next: _nextprev.next, back: _nextprev.back});
            return prev;
        });
    }, [getNextPrevSteps]);

    useEffect(()=>{
        loadTokenOptions();
    }, [loadTokenOptions]);


    useEffect(()=>{
        return ()=>{
            submittingRef.current = false;
            submissionBlockedRef.current = false;
            setStepData({});
            setCurrentStep({step: 1, next: null, back: null});
            setErrors(null);
            setSuccess(null);
        }
    }, []);

    return (
        <Modal show onHide={()=>{if (!submitting) onHide(success)}} size="xl" className={styles.modal} backdropClassName={styles.backdrop} backdrop="static">
            <Modal.Header closeButton/>
            <Modal.Body>
                <Container fluid>
                    <Row className="body d-flex">
                        <Col sm={12} lg={3} className="summary-bar">
                            <Summary {...props} stepData={stepData} stepUpdate={stepUpdateHandler} tokenOptions={tokenOptions} hasTokens={hasTokens}/>
                        </Col>
                        <Col>
                            <div className={styles.content}>
                                {tokenOptionsLoading ?
                                    <Container fluid className="d-flex align-items-center justify-content-center py-5" data-cy="refund-token-options-loading">
                                        <Spinner animation="border" variant="secondary" className="me-2" />
                                        <span>Loading refund and token options...</span>
                                    </Container>
                                : tokenOptionsError ?
                                    <Container fluid className="py-4" data-cy="refund-token-options-error">
                                        <h4 className={styles.title}>Refund options could not be loaded</h4>
                                        <p>{tokenOptionsError}</p>
                                        <Button type="button" variant="primary" onClick={loadTokenOptions}>Try Again</Button>
                                    </Container>
                                : success ? <Success /> :
                                    <>
                                        {submissionBlocked &&
                                            <Alert variant="warning" data-cy="refund-outcome-ambiguous">
                                                The refund result could not be confirmed. Close this window and refresh the order before trying again.
                                            </Alert>
                                        }
                                        {currentStep.step === 1 && <Step1 {...props} hasTokens={hasTokens} stepId={1} stepUpdate={stepUpdateHandler} stepData={stepData} />}
                                        {currentStep.step === 2 && <ByAmount {...props} hasTokens={hasTokens} stepId={2} stepUpdate={stepUpdateHandler} stepData={stepData} />}
                                        {currentStep.step === 3 && <TokenHandling {...props} tokenOptions={tokenOptions} stepId={3} stepUpdate={stepUpdateHandler} stepData={stepData} />}
                                        {currentStep.step === 4 && <ByTransaction {...props} stepId={4} stepUpdate={stepUpdateHandler} stepData={stepData} />}
                                        {currentStep.step === 5 && <Items {...props} stepId={5} stepUpdate={stepUpdateHandler} stepData={stepData} />}
                                        {currentStep.step === 6 && <AdminFee {...props} stepId={6} stepUpdate={stepUpdateHandler} stepData={stepData} />}
                                        {currentStep.step === 7 && <Register {...props} stepId={7} stepUpdate={stepUpdateHandler} stepData={stepData} />}
                                        {currentStep.step === 8 && <Memo {...props} stepId={8} stepUpdate={stepUpdateHandler} stepData={stepData} />}
                                        {currentStep.step === 9 && <Summary {...props} tokenOptions={tokenOptions} hasTokens={hasTokens} stepId={9} stepUpdate={stepUpdateHandler} stepData={stepData} full />}
                                    </>
                                }
                            </div>
                            {errors && <ErrorCatcher show error={errors} onClose={()=>setErrors(null)}/>}
                            {!props.registerId &&
                                <Buttons ref={buttonsRef} step={currentStep} maxSteps={maxSteps} onStepChange={stepChangeHandler} onHide={()=>onHide(success)} success={Boolean(success) ? allDoneHandler : null} submitting={submitting} blocked={submissionBlocked} submitLabel={hasTokens ? "Process Refund & Token Mediation" : "Process Refund"}/>
                            }
                        </Col>
                    </Row>
                </Container>
            </Modal.Body>
            {props.registerId && // style it for the pos
                <Modal.Footer className="p-0">
                    <Buttons ref={buttonsRef} step={currentStep} maxSteps={maxSteps} onStepChange={stepChangeHandler} onHide={()=>onHide(success)} registerId={props.registerId} success={Boolean(success) ? allDoneHandler : null} submitting={submitting} blocked={submissionBlocked} submitLabel={hasTokens ? "Process Refund & Token Mediation" : "Process Refund"}/>
                </Modal.Footer>
            }
        </Modal>
    );    
}
